import { getSettingElevateTopShopV2 } from "@faire/deprecated-retailer-visitor-shared/settings/getSettingElevateTopShopV2";
import { useSettingReviewsHoldoutXP } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingReviewsHoldoutXP";
import { useTopOfPageContent } from "@faire/deprecated-retailer-visitor-shared/stores/ui/useTopOfPageContent";
import { goToSmoothly } from "@faire/deprecated-retailer-visitor-shared/views/brand/utils/urlUtils";
import { IBrandReviewSummary } from "@faire/deprecated-retailer-visitor-shared/views/product/utils/interfaces";
import { useClickToScrollToReviewsDisplayTracking } from "@faire/deprecated-retailer-visitor-shared/views/reviews/useClickToScrollToReviewsDisplayTracking";
import { Core } from "@faire/design-tokens";
import { useLocalNum } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Star } from "@faire/web/slate/icons/foundation/Star";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography, TypographyVariants } from "@faire/web/slate/Typography";
import React from "react";

interface IProps {
  typographyVariant?: TypographyVariants;
  /**
   * The component to wrap the brand review summary in.
   * Defaults to a React fragment.
   */
  wrapper?: React.ComponentType<React.PropsWithChildren>;
  brandReviewSummary: IBrandReviewSummary;
  isDesktopAndAbove: boolean;
}

export const MaybeBrandReviewSummary = ({
  typographyVariant,
  wrapper: Wrapper,
  brandReviewSummary,
  isDesktopAndAbove,
}: IProps) => {
  const { overallRatingText, numberOfReviews } = brandReviewSummary;

  if (!overallRatingText || !numberOfReviews) {
    return null;
  }

  return (
    <BrandReviewSummary
      typographyVariant={typographyVariant}
      wrapper={Wrapper}
      overallRatingText={overallRatingText}
      numberOfReviews={numberOfReviews}
      isDesktopAndAbove={isDesktopAndAbove}
    />
  );
};

const BrandReviewSummary = ({
  typographyVariant,
  wrapper: Wrapper = React.Fragment,
  overallRatingText,
  numberOfReviews,
  isDesktopAndAbove,
}: Omit<IProps, "brandReviewSummary"> & {
  overallRatingText: string;
  numberOfReviews: number;
}) => {
  const isRetailerInHoldoutGroup = useSettingReviewsHoldoutXP();
  const isLinkToReviewsHidden = isDesktopAndAbove && isRetailerInHoldoutGroup;

  return (
    <Wrapper>
      <Star
        fontSize={getSettingElevateTopShopV2() ? "12px" : "13px"}
        fill={Core.icon.default}
        color={Core.icon.default}
      />

      <Spacer size={4} />

      <Typography variant={typographyVariant}>
        <span>{overallRatingText}</span>
        {isLinkToReviewsHidden ? null : (
          <>
            &nbsp;
            <LinkToReviewsSection
              typographyVariant={typographyVariant}
              numberOfReviews={numberOfReviews}
            />
          </>
        )}
      </Typography>
    </Wrapper>
  );
};

interface ILinkProps {
  numberOfReviews: number;
  typographyVariant?: TypographyVariants;
}

const LinkToReviewsSection: React.FC<ILinkProps> = ({
  numberOfReviews,
  typographyVariant,
}) => {
  const { headerPlusPromoBannerHeight } = useTopOfPageContent();
  const { localNum } = useLocalNum();
  const trackClickEvent = useClickToScrollToReviewsDisplayTracking();

  return (
    <div className="inline-block">
      (
      <Button
        variant="tertiary"
        onClick={(e) => {
          e.preventDefault();
          goToSmoothly("brand-reviews-summary", headerPlusPromoBannerHeight);
          trackClickEvent();
        }}
      >
        <Typography variant={typographyVariant}>
          {localNum(numberOfReviews)}
        </Typography>
      </Button>
      )
    </div>
  );
};
